import { useEffect, useState } from "react";
import { CornerDownLeft, Eye, EyeOff } from "lucide-react";
import { registerUser } from "../../../api/services/authService";
import DatePicker from "../../../components/common/DatePicker";
import AuthTerminalModal from "./AuthTerminalModal";
import { PromptField, Trace, TraceLine } from "./TerminalParts";
import {
  buttonClass,
  ghostButtonClass,
  inputClass,
  useSequence,
} from "./terminalUi";

const BOOT_STEPS = ["starting account setup"];
const BOOT_INTERVAL_MS = 260;

const CREATE_STEPS = ["validating fields", "creating account", "sending verification"];
const CREATE_INTERVAL_MS = 600;

/* Mirrors RegisterRequest: student sends `dob`, professional `designation`. */
const ROLES = {
  student: {
    blurb: "personal email · date of birth",
    emailLabel: "personal_email",
    field: { name: "dob", label: "date_of_birth", type: "date" },
    submit: "create account",
  },
  professional: {
    blurb: "work email · job title",
    emailLabel: "professional_email",
    field: {
      name: "designation",
      label: "designation",
      type: "text",
      placeholder: "e.g. Security Analyst",
    },
    submit: "complete setup",
  },
};

const MIN_PASSWORD = 8;
const CURRENT_YEAR = new Date().getFullYear();
const USERNAME_PATTERN = /^[a-z][a-z0-9_]*$/i;

const isEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
const isUsername = (value) =>
  value.length >= 3 && value.length <= 30 && USERNAME_PATTERN.test(value);

const EMPTY_FORM = {
  email: "",
  first_name: "",
  last_name: "",
  username: "",
  dob: "",
  designation: "",
  password: "",
  confirm_password: "",
};

/* Live `✓ / ✗` beside a label; null while the field is untouched. */
const check = (value, valid) => {
  if (!value) return null;
  return valid ? "✓ valid" : "✗ invalid";
};

function RoleOption({ index, name, blurb, selected, onSelect }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`flex w-full items-center gap-3.5 rounded-md border px-4 py-3.5 text-left transition-colors ${
        selected
          ? "border-(--color-primary) bg-[color-mix(in_srgb,var(--color-primary)_8%,transparent)]"
          : "lp-term-rule hover:border-(--color-primary)"
      }`}
    >
      <span
        className={`lp-term-rule grid h-7 w-7 flex-none place-items-center rounded border text-[12px] ${
          selected
            ? "border-(--color-primary) text-(--color-primary)"
            : "text-(--lp-term-dim)"
        }`}
      >
        {index}
      </span>
      <span className="flex-1">
        <span className="block text-[14px] text-(--lp-term-fg)">{name}</span>
        <span className="block text-[11px] text-(--lp-term-dim)">{blurb}</span>
      </span>
      {selected && (
        <span className="text-[11px] text-(--color-primary)">⏎ selected</span>
      )}
    </button>
  );
}

export default function SignupTerminal({ onClose, onSwitchToLogin }) {
  /* "role" → account type, "form" → fields, "running" → trace, "done" */
  const [phase, setPhase] = useState("role");
  const [role, setRole] = useState("student");
  const [form, setForm] = useState(EMPTY_FORM);
  const [showPassword, setShowPassword] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState("");
  const [created, setCreated] = useState(false);

  const bootStep = useSequence(BOOT_STEPS.length, BOOT_INTERVAL_MS);
  const createStep = useSequence(
    CREATE_STEPS.length,
    CREATE_INTERVAL_MS,
    phase === "running",
  );
  const finished = phase === "done";
  const config = ROLES[role];
  const isDateField = config.field.type === "date";

  // 1 / 2 pick the account type, matching the numbered rows.
  useEffect(() => {
    if (phase !== "role") return;
    const onKeyDown = (event) => {
      if (event.key === "1") setRole("student");
      if (event.key === "2") setRole("professional");
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [phase]);

  // Hold success until the trace has narrated its way to the last line.
  useEffect(() => {
    if (phase !== "running" || !created) return;
    if (createStep < CREATE_STEPS.length * 2 - 2) return;
    setPhase("done");
  }, [phase, created, createStep]);

  const handleChange = ({ target }) => {
    setForm((current) => ({ ...current, [target.name]: target.value }));
    setError("");
  };

  const trackCapsLock = (event) => {
    setCapsLockOn(event.getModifierState?.("CapsLock") ?? false);
  };

  const passwordsMatch =
    form.password.length > 0 && form.password === form.confirm_password;

  const required = [
    isEmail(form.email),
    form.first_name.trim().length > 0,
    form.last_name.trim().length > 0,
    isUsername(form.username),
    form[config.field.name].trim().length > 0,
    form.password.length >= MIN_PASSWORD,
    passwordsMatch,
  ];
  const complete = required.filter(Boolean).length;
  const canSubmit = complete === required.length && agreed;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setCreated(false);
    setPhase("running");

    try {
      await registerUser({
        email: form.email,
        username: form.username,
        first_name: form.first_name,
        last_name: form.last_name,
        password: form.password,
        confirm_password: form.confirm_password,
        role,
        [config.field.name]: form[config.field.name],
      });
      setCreated(true);
    } catch (submitError) {
      setPhase("form");
      setError(submitError.message || "Could not create the account");
    }
  };

  const command =
    phase === "role" ? "qv auth --signup" : `qv auth --signup --role ${role}`;

  const passwordProps = {
    type: showPassword ? "text" : "password",
    onChange: handleChange,
    onKeyDown: trackCapsLock,
    onKeyUp: trackCapsLock,
    placeholder: "••••••••",
    autoComplete: "new-password",
    className: inputClass,
    required: true,
  };

  return (
    <AuthTerminalModal
      onClose={onClose}
      labelledBy="lp-signup-command"
      status={finished ? "account created" : "session live"}
    >
      <p id="lp-signup-command" className="qv-mono text-[12.5px]">
        <span className="text-(--color-primary)">root@quantum-vanguard</span>
        <span className="text-(--lp-term-dim)">:~#</span>{" "}
        <span className="text-(--lp-term-fg)">{command}</span>
      </p>

      {phase === "role" && (
        <>
          <div className="qv-mono mt-3 space-y-1.5">
            {bootStep > 0 && (
              <TraceLine label={BOOT_STEPS[0]} done={bootStep > 1} />
            )}
          </div>

          <div className="qv-mono mt-6 flex flex-wrap items-center gap-3">
            <span className="text-[11.5px] text-(--lp-term-dim)">mode:</span>
            <button
              type="button"
              onClick={onSwitchToLogin}
              className="lp-term-rule rounded-md border border-transparent px-4 py-1.5 text-[12.5px] text-(--lp-term-dim) transition-colors hover:border-(--color-primary) hover:text-(--color-primary)"
            >
              [ login ]
            </button>
            <span className="rounded-md border border-(--color-primary) bg-[color-mix(in_srgb,var(--color-primary)_10%,transparent)] px-4 py-1.5 text-[12.5px] text-(--color-primary)">
              [ signup ]
            </span>
          </div>

          <p className="qv-mono mt-6 text-[11.5px] lowercase tracking-wide text-(--color-primary)">
            select account_type
          </p>

          <div className="qv-mono mt-3 space-y-2.5">
            {Object.entries(ROLES).map(([key, value], index) => (
              <RoleOption
                key={key}
                index={index + 1}
                name={key}
                blurb={value.blurb}
                selected={role === key}
                onSelect={() => setRole(key)}
              />
            ))}
          </div>

          <p className="qv-mono lp-term-rule mt-4 rounded-md border border-dashed px-4 py-3 text-[11.5px] text-(--lp-term-dim)">
            ▸ next: {required.length} fields — email, name, username,{" "}
            {config.field.label}, password
          </p>

          {/* a form so Enter continues, as the hint below promises */}
          <form
            onSubmit={(event) => {
              event.preventDefault();
              setPhase("form");
            }}
            className="mt-5 flex gap-3"
          >
            <button type="submit" className={`qv-mono ${buttonClass}`}>
              <CornerDownLeft className="h-4 w-4" />
              continue
            </button>
            <button
              type="button"
              onClick={onClose}
              className={`qv-mono ${ghostButtonClass}`}
            >
              esc
            </button>
          </form>

          <p className="qv-mono mt-4 text-center text-[11px] text-(--lp-term-dim)">
            1-2 pick · ⏎ continue
          </p>
        </>
      )}

      {phase === "form" && (
        <>
          <p className="qv-mono mt-3 flex flex-wrap items-center gap-2.5 text-[12px]">
            <span className="text-(--color-primary)">✓</span>
            <span className="text-(--lp-term-dim)">role locked · {role}</span>
            <button
              type="button"
              onClick={() => setPhase("role")}
              className="lp-term-rule rounded border px-2.5 py-1 text-[11px] text-(--lp-term-dim) transition-colors hover:border-(--color-primary) hover:text-(--color-primary)"
            >
              change
            </button>
            <span className="ml-auto text-[11px] text-(--lp-term-dim)">
              {complete} / {required.length} complete
            </span>
          </p>

          <form onSubmit={handleSubmit} className="qv-mono mt-6 space-y-5">
            <PromptField
              label={config.emailLabel}
              required
              status={check(form.email, isEmail(form.email))}
            >
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="you@company.com"
                autoComplete="email"
                className={inputClass}
                required
                autoFocus
              />
            </PromptField>

            <div className="grid gap-5 sm:grid-cols-2">
              <PromptField label="first_name" required>
                <input
                  type="text"
                  name="first_name"
                  value={form.first_name}
                  onChange={handleChange}
                  placeholder="First Name"
                  autoComplete="given-name"
                  className={inputClass}
                  required
                />
              </PromptField>

              <PromptField label="last_name" required>
                <input
                  type="text"
                  name="last_name"
                  value={form.last_name}
                  onChange={handleChange}
                  placeholder="Last Name"
                  autoComplete="family-name"
                  className={inputClass}
                  required
                />
              </PromptField>
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <PromptField
                label="username"
                required
                status={check(form.username, isUsername(form.username))}
              >
                <input
                  type="text"
                  name="username"
                  value={form.username}
                  onChange={handleChange}
                  placeholder="Username"
                  autoComplete="username"
                  className={inputClass}
                  required
                />
              </PromptField>

              <PromptField
                label={config.field.label}
                required
                bare={isDateField}
              >
                {isDateField ? (
                  <DatePicker
                    name={config.field.name}
                    value={form[config.field.name]}
                    onChange={handleChange}
                    placeholder="dd/mm/yyyy"
                    required
                    size="small"
                    maxYear={CURRENT_YEAR}
                    margin=""
                  />
                ) : (
                  <input
                    type={config.field.type}
                    name={config.field.name}
                    value={form[config.field.name]}
                    onChange={handleChange}
                    placeholder={config.field.placeholder}
                    className={inputClass}
                    required
                  />
                )}
              </PromptField>
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <PromptField
                label="password"
                required
                status={`min ${MIN_PASSWORD} chars`}
                trailing={
                  <button
                    type="button"
                    onClick={() => setShowPassword((visible) => !visible)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    className="text-(--lp-term-dim) transition-colors hover:text-(--color-primary)"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                }
              >
                <input
                  {...passwordProps}
                  name="password"
                  value={form.password}
                  minLength={MIN_PASSWORD}
                />
              </PromptField>

              <PromptField
                label="confirm_password"
                required
                status={check(form.confirm_password, passwordsMatch)}
              >
                <input
                  {...passwordProps}
                  name="confirm_password"
                  value={form.confirm_password}
                />
              </PromptField>
            </div>

            {capsLockOn && (
              <p className="lp-term-line text-[11px] text-[#ef9f27]">
                ▸ caps lock is on
              </p>
            )}

            <label className="lp-term-rule flex cursor-pointer items-center gap-3 rounded-md border px-4 py-3 text-[12px] text-(--lp-term-dim)">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(event) => setAgreed(event.target.checked)}
                className="h-4 w-4"
                style={{ accentColor: "var(--color-primary)" }}
              />
              I agree to the{" "}
              <span className="text-(--color-primary)">Terms of Service</span>{" "}
              and{" "}
              <span className="text-(--color-primary)">Privacy Policy</span>
            </label>

            {error && (
              <p className="lp-term-line text-[12px] text-(--lp-term-err)">
                ▸ error: {error}
              </p>
            )}

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setPhase("role")}
                className={ghostButtonClass}
              >
                ← back
              </button>
              <button
                type="submit"
                disabled={!canSubmit}
                className={buttonClass}
              >
                <CornerDownLeft className="h-4 w-4" />
                {config.submit}
              </button>
            </div>
          </form>

          <p className="qv-mono mt-5 text-center text-[12px] text-(--lp-term-dim)">
            already registered?{" "}
            <button
              type="button"
              onClick={onSwitchToLogin}
              className="text-(--color-primary) transition-opacity hover:opacity-80"
            >
              qv auth --login
            </button>
          </p>
        </>
      )}

      {(phase === "running" || finished) && (
        <div className="mt-4">
          <Trace steps={CREATE_STEPS} step={createStep} finished={finished} />

          {finished && (
            <div className="qv-mono lp-term-line mt-5 space-y-4">
              <p className="text-[13px] text-(--lp-term-fg)">
                ▸ account created ·{" "}
                <span className="text-(--color-primary)">@{form.username}</span>{" "}
                · {role}
              </p>
              <p className="text-[12px] leading-relaxed text-(--lp-term-dim)">
                Verify your email before signing in — we sent a link to{" "}
                <span className="text-(--color-primary)">{form.email}</span>.
              </p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onSwitchToLogin}
                  className={buttonClass}
                >
                  <CornerDownLeft className="h-4 w-4" />
                  go to login
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className={ghostButtonClass}
                >
                  esc
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </AuthTerminalModal>
  );
}
